import { solveSorting, SortingModel, type SortingRules } from "@nano/puzzle-core";
import { z } from "zod";
import { chapterOfLevel } from "./chapters.ts";
import { TUTORIAL_BY_ID } from "./tutorials.ts";
import {
  baseTagsOf,
  COLOR_TINTS,
  isKnownTag,
  ITEM_KIND_BY_ASSET,
  SIZE_RANK,
  type SortColor,
  type SortSize,
} from "./vocabulary.ts";

export const SORTING_HOUSE_ID = "sorting" as const;

const tagSchema = z.string().regex(/^(type|shape|color|size|special):[a-z-]+$/);

export const sortingItemSchema = z
  .object({
    id: z.string().regex(/^i\d{1,2}$/),
    assetId: z.string().regex(/^item\.[a-z-]+$/),
    tags: z.array(tagSchema).min(1).max(6),
    rank: z.number().int().min(1).max(4),
    tint: z
      .string()
      .regex(/^#[0-9a-f]{6}$/)
      .optional(),
    special: z.literal("switch").optional(),
  })
  .strict();

export const sortingBinSchema = z
  .object({
    id: z.string().regex(/^[a-z][a-z0-9-]{0,15}$/),
    capacity: z.number().int().min(1).max(12),
    accepts: z
      .array(z.object({ all: z.array(tagSchema).max(3) }).strict())
      .min(1)
      .max(3),
    order: z.enum(["asc", "desc"]).optional(),
    pedestal: z.boolean().optional(),
    style: z.enum(["basket", "jar", "crate", "pedestal"]).optional(),
  })
  .strict();

export const sortingRulesSchema = z
  .object({
    layout: z.enum(["shelf", "queue"]),
    visible: z.number().int().min(1).max(6).optional(),
    bins: z.array(sortingBinSchema).min(2).max(7),
    items: z.array(sortingItemSchema).min(2).max(24),
    maxMoves: z.number().int().min(2).max(120).optional(),
  })
  .strict();

export const sortingLevelSchema = z
  .object({
    id: z.number().int().min(1).max(100),
    houseId: z.literal(SORTING_HOUSE_ID),
    contentVersion: z.string().regex(/^sorting-\d+\.\d+\.\d+$/),
    chapter: z.number().int().min(1).max(10),
    difficulty: z.union([z.literal(1), z.literal(2), z.literal(3), z.literal(4), z.literal(5)]),
    themeId: z.string().min(1),
    tutorialId: z.string().min(1).optional(),
    /** Optimal action count proven by the solver at pack build time. */
    optimalMoves: z.number().int().min(1),
    targetMoves: z.number().int().min(1),
    maxMistakesForThreeStars: z.number().int().min(0).max(5),
    seed: z.number().int().nonnegative().optional(),
    rules: sortingRulesSchema,
  })
  .strict();

export type SortingLevel = z.infer<typeof sortingLevelSchema>;

export const sortingPackSchema = z
  .object({
    packVersion: z.literal(1),
    houseId: z.literal(SORTING_HOUSE_ID),
    contentVersion: z.string(),
    chapter: z.number().int().min(1).max(10),
    levels: z.array(sortingLevelSchema).length(10),
  })
  .strict();
export type SortingPack = z.infer<typeof sortingPackSchema>;

/** The engine-facing view of a level (drops the content-only fields). */
export function toRules(level: SortingLevel): SortingRules {
  return level.rules as SortingRules;
}

export interface LevelIssue {
  readonly levelId: number;
  readonly message: string;
}

/**
 * Semantic checks beyond the schema: tags match the art, ids are unique,
 * the switch mechanic is wired correctly, every item has a home, and the
 * solver proves the level is winnable in exactly `optimalMoves`.
 */
export function validateSortingLevel(
  level: SortingLevel,
  options: { solve?: boolean; maxNodes?: number } = {},
): LevelIssue[] {
  const issues: LevelIssue[] = [];
  const fail = (message: string) => issues.push({ levelId: level.id, message });
  const { rules } = level;

  if (chapterOfLevel(level.id).chapter !== level.chapter) {
    fail(`chapter ${level.chapter} does not match level id`);
  }
  if (chapterOfLevel(level.id).themeId !== level.themeId) {
    fail(`theme ${level.themeId} does not match chapter`);
  }
  if (level.tutorialId && !TUTORIAL_BY_ID.has(level.tutorialId)) {
    fail(`unknown tutorial ${level.tutorialId}`);
  }

  const itemIds = new Set<string>();
  for (const item of rules.items) {
    if (itemIds.has(item.id)) fail(`duplicate item id ${item.id}`);
    itemIds.add(item.id);

    const kindDef = ITEM_KIND_BY_ASSET.get(item.assetId);
    if (!kindDef) {
      fail(`${item.id}: unknown asset ${item.assetId}`);
      continue;
    }
    for (const tag of item.tags) {
      if (!isKnownTag(tag)) fail(`${item.id}: unknown tag ${tag}`);
    }
    for (const tag of baseTagsOf(kindDef)) {
      if (!item.tags.includes(tag)) fail(`${item.id}: missing base tag ${tag} for ${kindDef.id}`);
    }
    const colorTags = item.tags.filter((tag) => tag.startsWith("color:"));
    if (colorTags.length > 1) fail(`${item.id}: more than one color`);
    const color = colorTags[0]?.slice("color:".length) as SortColor | undefined;
    if (color && !kindDef.tintable) fail(`${item.id}: ${kindDef.id} cannot be recoloured`);
    if (color && item.tint !== COLOR_TINTS[color]) fail(`${item.id}: tint does not match ${color}`);
    if (!color && item.tint) fail(`${item.id}: tint without a color tag`);

    const sizeTags = item.tags.filter((tag) => tag.startsWith("size:"));
    if (sizeTags.length > 1) fail(`${item.id}: more than one size`);
    const size = sizeTags[0]?.slice("size:".length) as SortSize | undefined;
    if (size && SIZE_RANK[size] !== item.rank) fail(`${item.id}: rank ${item.rank} does not match size ${size}`);

    const isSwitch = kindDef.special === "switch";
    if (isSwitch !== (item.special === "switch")) fail(`${item.id}: special flag must match kind`);
    if (item.tags.some((tag) => tag.startsWith("shape:")) && !kindDef.shape) {
      fail(`${item.id}: ${kindDef.id} has no shape`);
    }
  }

  const binIds = new Set<string>();
  const switchCount = rules.items.filter((item) => item.special === "switch").length;
  const pedestalCapacity = rules.bins
    .filter((bin) => bin.pedestal)
    .reduce((sum, bin) => sum + bin.capacity, 0);
  for (const bin of rules.bins) {
    if (binIds.has(bin.id)) fail(`duplicate bin id ${bin.id}`);
    binIds.add(bin.id);
    if (bin.pedestal) {
      if (bin.style !== "pedestal") fail(`${bin.id}: pedestal needs pedestal style`);
      continue;
    }
    if (bin.accepts.length > switchCount + 1) fail(`${bin.id}: more rule phases than switch items`);
    for (const predicate of bin.accepts) {
      if (predicate.all.length === 0) fail(`${bin.id}: empty predicate accepts everything`);
      for (const tag of predicate.all) {
        if (!isKnownTag(tag) || tag.startsWith("special:")) fail(`${bin.id}: bad rule tag ${tag}`);
      }
    }
    if (bin.order && bin.style !== "jar") fail(`${bin.id}: stacked bins use the jar style`);
  }
  if (switchCount > 0 && pedestalCapacity !== switchCount) {
    fail(`pedestal capacity ${pedestalCapacity} must equal switch items ${switchCount}`);
  }
  if (switchCount === 0 && pedestalCapacity > 0) fail("pedestal without switch items");

  const normalCapacity = rules.bins.filter((bin) => !bin.pedestal).reduce((sum, bin) => sum + bin.capacity, 0);
  const normalItems = rules.items.length - switchCount;
  if (normalCapacity < normalItems) fail(`capacity ${normalCapacity} < items ${normalItems}`);

  if (rules.layout === "queue" && rules.visible === undefined) fail("queue layout needs visible");
  if (rules.layout === "shelf" && rules.visible !== undefined) fail("shelf layout must not set visible");

  if (level.targetMoves < level.optimalMoves) fail("targetMoves below optimal");
  if (rules.maxMoves !== undefined && rules.maxMoves < level.targetMoves) fail("maxMoves below targetMoves");

  if (issues.length === 0 && options.solve !== false) {
    try {
      const result = solveSorting(new SortingModel(toRules(level)), {
        maxNodes: options.maxNodes ?? 400_000,
      });
      if (result.status !== "solved") {
        fail(`solver: ${result.status} after ${result.explored} states`);
      } else if (result.optimalActions !== level.optimalMoves) {
        fail(`solver optimal ${result.optimalActions} != declared ${level.optimalMoves}`);
      }
    } catch (error) {
      fail(`engine rejected level: ${(error as Error).message}`);
    }
  }
  return issues;
}

/** Structural fingerprint used to prove levels are not copies of each other. */
export function levelFingerprint(level: SortingLevel): string {
  const items = level.rules.items
    .map((item) => `${item.assetId}|${[...item.tags].sort().join(",")}|${item.rank}`)
    .sort()
    .join(";");
  const bins = level.rules.bins
    .map((bin) => `${bin.accepts.map((p) => [...p.all].sort().join("+")).join(">")}@${bin.capacity}${bin.order ?? ""}`)
    .sort()
    .join(";");
  return `${level.rules.layout}:${level.rules.visible ?? 0}:${level.rules.maxMoves ?? 0}:${bins}:${items}`;
}
